/**
 * @file ChatBubble.jsx
 * @description Chat bubble component that opens a chat interface
 */

import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, TextField, Paper } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: 'Hello! How can we help you today?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (message.trim()) {
      const newMessage = {
        id: messages.length + 1,
        text: message.trim(),
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages([...messages, newMessage]);
      setMessage('');
      
      // Optional: Add a bot response after a short delay
      setTimeout(() => {
        const botResponse = {
          id: messages.length + 2,
          text: 'Thank you for your message! We will get back to you soon.',
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, botResponse]);
      }, 1000);
    }
  };

  return (
    <>
      {/* Chat Bubble Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
        }}
      >
        {!isOpen && (
          <IconButton
            onClick={() => setIsOpen(true)}
            sx={{
              backgroundColor: '#ed7b0b',
              color: '#ffffff',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              '&:hover': {
                backgroundColor: '#d66a0a',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.3s ease',
            }}
            aria-label="Open chat"
          >
            <ChatIcon sx={{ fontSize: '28px' }} />
          </IconButton>
        )}

        {/* Chat Window */}
        {isOpen && (
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              width: '350px',
              height: '500px',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '12px',
              overflow: 'hidden',
              '@media (max-width: 480px)': {
                width: 'calc(100vw - 40px)',
                height: 'calc(100vh - 40px)',
                bottom: '20px',
                right: '20px',
                left: '20px',
              },
            }}
          >
            {/* Chat Header */}
            <Box
              sx={{
                backgroundColor: '#ed7b0b',
                color: '#ffffff',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ChatIcon />
                <strong>Chat with us</strong>
              </Box>
              <IconButton
                onClick={() => setIsOpen(false)}
                sx={{ color: '#ffffff', padding: '4px' }}
                aria-label="Close chat"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Chat Messages Area */}
            <Box
              sx={{
                flex: 1,
                padding: '16px',
                overflowY: 'auto',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              {messages.map((msg) => (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '75%',
                      padding: '10px 14px',
                      borderRadius: msg.sender === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      backgroundColor: msg.sender === 'user' ? '#ed7b0b' : '#ffffff',
                      color: msg.sender === 'user' ? '#ffffff' : '#333333',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {msg.sender === 'bot' && (
                      <Box
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#ed7b0b',
                          marginBottom: '4px',
                        }}
                      >
                        Bellefy Insights
                      </Box>
                    )}
                    <Box
                      sx={{
                        fontSize: '14px',
                        lineHeight: 1.4,
                        wordWrap: 'break-word',
                      }}
                    >
                      {msg.text}
                    </Box>
                    <Box
                      sx={{
                        fontSize: '10px',
                        opacity: 0.7,
                        marginTop: '4px',
                        textAlign: 'right',
                      }}
                    >
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Box>
                  </Box>
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Box>

            {/* Chat Input */}
            <Box
              sx={{
                padding: '12px',
                backgroundColor: '#ffffff',
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                gap: '8px',
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Type your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSend();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                  },
                }}
              />
              <IconButton
                onClick={handleSend}
                sx={{
                  backgroundColor: '#ed7b0b',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#d66a0a',
                  },
                }}
                aria-label="Send message"
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Paper>
        )}
      </Box>
    </>
  );
};

export default ChatBubble;
